<template>
  <section class="savings" :class="{ print: printing }">
    <h1>Årlig besparing</h1>
    <div class="row">
      <div class="progress-bar">
        <div class="bottom"></div>
      </div>
      <div class="price">
        {{ priceDifference }} <br v-if="printing"/><span>KR</span>
      </div>
    </div>
    <div class="row">
      <div class="wrap">
        <div class="row">
          <div class="slider-row">
            <div class="column">Körsträcka per år<span>{{ mileage | normalize }} mil</span></div>
            <div class="column">
              <div class="slider" id="mileage"></div>
            </div>
          </div>
          <div class="slider-row">
            <div class="column">Drivmedelspris<span>{{ highestValueOfCompanies | normalize | toDecimal }} kr/liter</span></div>
            <div class="column">
              <div class="slider" id="price"></div>
            </div>
          </div>
          <div class="slider-row">
            <div class="column">Antal fordon<span>{{ cars }} st</span></div>
            <div class="column">
              <div class="slider" id="cars"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="wrap explanation">
        <div class="row">
          <div class="placeholder" v-on:click="expanded = !expanded" :class="{ expanded: expanded }">
            Så här har vi räknat <div class="arrow"></div>
          </div>
          <transition v-on:enter="enter" v-on:leave="leave">
            <div class="text" v-if="expanded || printing">
              Spargeneratorn är baserad på ett fordon som har en medelförbrukning på 0,7 liter/mil (diesel). Uträkningen är baserad på mellanskillnaden mellan den dyraste aktörens pris och Tankas pris. Angivna drivmedelspriser på Drivmedelsguiden är inhämtade från respektive aktörs hemsida.</div>
          </transition>
        </div>
      </div>
    </div>
    <div>
      <div class="button-row">
        <div class="button" v-on:click="$emit('sendToPrinting')">Spara PDF</div>
      </div>
    </div>
  </section>
</template>

<script>
  import ProgressBar from 'progressbar.js'
  import sizzle from 'sizzle'
  import scrollMonitor from 'scrollmonitor'
  import Odometer from 'odometer'
  import noUiSlider from 'nouislider'
  import wNumb from 'wnumb'
  import scrollDown from 'lib/scrollDown'
  import Velocity from 'velocity-animate'

  export default {
    props: {
      companies: {
        type: Array
      },
      type: String,
      printing: {
        type: Boolean,
        default: false
      },
      savings: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        prg: null,
        od: null,
        sliders: {
          mileage: null,
          price: null,
          cars: null
        },
        timeoutId: null,
        expanded: false,
        target: null
      }
    },
    filters: {
      normalize (value) {
        const formatter = wNumb({
          mark: ',',
          thousand: ' '
        })
        return formatter.to(value)
      },
      toDecimal (value) {
        return parseFloat(value.replace(',', '.')).toFixed(2)
      }
    },
    mounted () {
      const that = this
      that.target = sizzle('.download')[0]
      const element = sizzle('.progress-bar', that.$el)[0]

      // Use ProgressBar.js library to render a circle progress bar
      that.prg = new ProgressBar.Circle(element, {
        strokeWidth: 4,
        color: '#178394',
        // trailColor: '#91f47c',
        text: {
          value: 0,
          className: 'progress-text',
          style: {
            color: '#178394',
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '80%',
            fontSize: '3.5em',
            padding: 0,
            margin: 0,
            transform: {
              prefix: true,
              value: 'translate(-50%, -50%)'
            }
          }
        },
        svgStyle: {
          display: 'block',
          width: '100%'
        }
      })

      // Initiate odometer inside text area
      that.od = new Odometer({
        el: that.prg.text,
        value: 0,
        duration: 800,
        format: '( ddd)',
        theme: 'minimal'
      })

      // Create a new div with the word "kr" to explain the number in odometer
      const newEl = document.createElement('div')
      newEl.innerHTML = 'KR'
      newEl.style.fontSize = '0.8em'
      that.od.el.appendChild(newEl)

      // Create a path for the white cover background
      const path = document.createElementNS(that.prg.svg.namespaceURI, 'path')
      path.setAttribute('d', 'M 52,52 m 0,-48 a 48,48 1 1 1 0,96 a 44,44 0 1 1 0,-96')
      path.setAttribute('fill', '#fff')
      path.setAttribute('transform', 'scale(0.96)')

      // Insert the path into the SVG image
      that.prg.svg.insertBefore(path, that.prg.path)

      // Create a slider for mileage
      that.sliders.mileage = noUiSlider.create(sizzle('#mileage', that.$el)[0], {
        start: that.mileage,
        connect: [true, false],
        step: 500,
        range: {
          min: 500,
          max: 10000
        },
        format: wNumb({
          decimals: 0
        })
      })

      // On slider update, update the mileage count
      that.sliders.mileage.on('update', (v) => {
        that.mileage = parseInt(v[0])
        that.animateProgress()
      })

      // Create a slider for price
      that.sliders.price = noUiSlider.create(sizzle('#price', that.$el)[0], {
        start: that.highestValueOfCompanies,
        connect: [true, false],
        step: 0.01,
        range: {
          min: that.tankaValue,
          max: that.tankaValue + 2
        },
        format: wNumb({
          decimals: 2
        })
      })

      that.sliders.price.on('update', (v) => {
        that.price = parseFloat(v[0])
        that.animateProgress()
      })

      // Create a slider for cars
      that.sliders.cars = noUiSlider.create(sizzle('#cars', that.$el)[0], {
        start: that.cars,
        connect: [true, false],
        step: 1,
        range: {
          min: 1,
          max: 20
        },
        format: wNumb({
          decimals: 0
        })
      })

      that.sliders.cars.on('update', (v) => {
        that.cars = parseInt(v[0])
        that.animateProgress()
      })

      if (this.printing) {
        that.animateForPrint()
        return
      }

      // Create a scroll watcher for an element at the bottom of the progressbar window
      const watcher = scrollMonitor.create(sizzle('.bottom', that.$el)[0])

      // Function when it enters viewport
      watcher.enterViewport(() => {
        that.animateInitial()
      })

      // When it exists viewport
      watcher.exitViewport(() => {
        that.resetProgress()
      })
    },
    methods: {
      enter (el, done) {
        Velocity(el, 'slideDown', {
          duration: 200,
          complete () {
            done()
          }
        })
      },
      leave (el, done) {
        Velocity(el, 'slideUp', {
          duration: 200,
          complete () {
            done()
          }
        })
      },
      scrollDown () {
        scrollDown(this.target.offsetTop)
      },
      calculateDifference () {
        return parseFloat(this.literPriceDifference / 2).toFixed(2)
      },
      animateForPrint () {
        setTimeout(() => {
          window.PhantomJSPrinting.readyToRender = true
        }, 500)
        this.od.options.duration = 10
        this.prg.animate(this.calculateDifference(), {
          duration: 10
        })
      },
      animateInitial () {
        // Reset values on enter
        this.price = 0
        this.cars = 1
        this.mileage = 2500
        this.od.update(0)

        // Animate progress
        this.animateProgress()

        // Change price slider option values
        this.sliders.price.options.range.min = this.tankaValue
        this.sliders.price.set(this.highestValueOfCompanies)
      },
      animateProgress () {
        const that = this
        that.prg.animate(that.calculateDifference(), {
          duration: 800
        })

        // Make Odometer wait for 0.5s before it
        clearTimeout(that.timeoutId)
        that.timeoutId = setTimeout(() => {
          that.od.update(that.priceDifference)
        }, 500)
      },
      resetProgress () {
        this.prg.animate(0, {
          duration: 200
        })
        this.price = 0
        this.cars = 1
        this.mileage = 2500
        this.od.update(0)
      }
    },
    computed: {
      highestProfit () {
        // return 20000
        return window.Math.ceil((this.highestValueOfCompanies - this.tankaValue) * 10000 * this.cars)
      },
      priceDifference () {
        return window.Math.ceil((this.highestValueOfCompanies - this.tankaValue) * this.mileage * this.cars * 0.7)
      },
      literPriceDifference () {
        return this.highestValueOfCompanies - this.tankaValue
      },
      tankaValue () {
        const that = this
        let result = 0
        this.companies.forEach((x) => {
          if (x.name === 'Tanka') {
            result = x.data[that.type] + x.data.cardDiscount + x.data.invoiceDiscount
          }
        })

        return parseFloat(result.toFixed(2))
      },
      highestValueOfCompanies: {
        get () {
          const that = this
          if (that.price !== 0) {
            return that.price
          }

          let result = 0
          let tot

          this.companies.forEach((x) => {
            const type = that.type

            if (type === 'dieselPrice') {
              tot = x.data.dieselPriceList + x.data.cardDiscount + x.data.invoiceDiscount
            } else {
              tot = x.data.gasPriceList + x.data.cardDiscount + x.data.invoiceDiscount
            }

            if (tot > 0) {
              result = tot
            }
          })
          return parseFloat(result.toFixed(2))
        },
        set (value) {
          this.price = value
        }
      },
      cars: {
        get () {
          return this.savings.cars
        },
        set (value) {
          this.$emit('updateSavings', {
            name: 'cars',
            value: value
          })
        }
      },
      mileage: {
        get () {
          return this.savings.mileage
        },
        set (value) {
          this.$emit('updateSavings', {
            name: 'mileage',
            value: value
          })
        }
      },
      price: {
        get () {
          return this.savings.price
        },
        set (value) {
          this.$emit('updateSavings', {
            name: 'price',
            value: value
          })
        }
      }
    }
  }
</script>

<style rel="stylesheet/scss" type="text/css" lang="scss">
  @import "~odometer/themes/odometer-theme-minimal.css";
  @import "~nouislider/distribute/nouislider.css";

  .noUi-connect {
    background: #178394;
  }

  .noUi-horizontal .noUi-handle {
    border: 2px solid #fff;
    box-shadow: none;
    background: #178394;
    border-radius: 20px;
    height: 34px !important;
    width: 34px !important;
    cursor: pointer;
    outline: none;
    top: -10px;

    &:before, &:after {
      display: none;
    }
  }

  .noUi-base {
    background: #e7e7e7;
    border: none;
  }

  section.print {
    .progress-text {
      font-size: 3em !important;
    }
  }
</style>

<style rel="stylesheet/scss" type="text/css" lang="scss" scoped>
  @import "~styles/button.scss";

  section {
    background: #ffffff;
    width: 100%;
    min-height: 100%;
    overflow: hidden;
    text-align: center;
    padding-top: 30px;
    padding-bottom: 40px;
    position: relative;

    &.print {
      background: transparent;
      max-width: 540px;
      width: auto;
      display: inline-block;
      margin: 20px 10px 0 10px;
      padding-bottom: 0;
      min-height: 0;

      h1 {
        font-size: 1.2em;
        text-align: left;
        margin-bottom: 20px;
        margin-top: 0;
        padding-left: 5px;
      }

      .row:nth-child(2) {
        float: left;
        padding-top: 0;

        .progress-bar {
          display: none;
        }

        .price {
          background: url('~assets/images/cirkelDone.png') center center;
          background-size: cover;
          display: inline-block;
          font-size: 2em;
          color: #178394;
          font-weight: bold;
          height: 100px;
          padding-top: 51px;
          width: 150px;
          vertical-align: middle;
          line-height: 25px;

          span {
            font-size: 0.6em;
          }
        }
      }

      .row:nth-child(3) {
        text-align: left;
        float: left;
        padding-top: 10px;
        margin-left: 80px;

        .wrap {
          margin-top: 0;
        }

        .slider-row {
          margin-bottom: 0;
          padding: 3px;
        }

        .column {
          margin-top: 10px;
          text-align: left;
        }

        .column:nth-child(2) {
          display: none;
        }
      }

      .row:nth-child(4) {
        width: 100%;

        .wrap {
          width: 100%;
          margin-top: 50px;
          max-width: none;
          font-size: 0.8em;

          .placeholder {
            text-align: center;
          }

          .arrow {
            display: none;
          }

          .text {
            text-align: center;
            margin-top: 0;
          }
        }
      }

      .button-row {
        display: none;
      }

      .footer-wrap {
        display: block;
        margin-top: 140px;
      }
    }

    .wrap {
      display: inline-block;
      max-width: 600px;
      height: 100%;
      width: 100%;
      margin-top: 45px;

      @media screen and (max-height: 700px) {
        margin-top: 5px;
      }
    }

    h1 {
      color: #000;
      margin-top: 50px;
      font-size: 2.5em;
      letter-spacing: 0.03em;
    }

    .progress-bar {
      display: inline-block;
      width: 300px;
      height: 300px;
      margin-top: 30px;
      position: relative;
      background: url('~assets/images/cirkel.svg') center center;
      background-size: cover;

      .bottom {
        bottom: 0;
        height: 1px;
        width: 1px;
        position: absolute;
      }
    }

    .price {
      display: none;
    }

    .progress-text {
      background: #3d3bcc;
    }

    .slider-row {
      text-align: left;
      margin-bottom: 10px;
      padding: 10px 20px;

      @media screen and (max-width: 700px) {
        margin: 0;
        padding: 5px 20px;
      }

      .column {
        text-align: center;
        width: 100%;
        font-size: 1.3em;

        span {
          margin-left: 10px;
          font-weight: bold;
        }

        .slider {
          margin-top: 5px;
        }
      }
    }

    .placeholder, .text {
      text-align: left;
      padding: 0 15px;
    }

    .explanation {
      width: 100%;
      max-width: 600px;
    }

    .placeholder {
      font-weight: bold;
      cursor: pointer;
      text-align: center;

      .arrow {
        background: url('~assets/images/arrowDown.svg') no-repeat;
        width: 20px;
        height: 10px;
        display: inline-block;
        transform:scaleY(1);
      }

      &.expanded .arrow {
        transform:scaleY(-1);
      }
    }

    .text {
      margin-top: 20px;
    }

    .button-row {
      .button {
        color: #178394;
        border: 1px solid #178394;
      }

      .button:hover {
        color: #ffffff;
        background: #178394;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// savings.vue?575bd211